import { useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { AdminAddMemberRequest, GarageMemberRole } from '../types/admin-garage-edit';
import UsernameAvailability from './UsernameAvailability';

const ROLES: GarageMemberRole[] = ['MANAGER', 'STAFF'];

interface AddGarageMemberDialogProps {
  garageId: string;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Creates a new user that is born belonging to this garage. Single mode by design
 * (Spec 039): admins never search or move users across garages from this surface —
 * cross-garage user lookup lives at /admin/users.
 */
export default function AddGarageMemberDialog({ garageId, onClose, onSaved }: AddGarageMemberDialogProps) {
  const { token } = useAuth();
  const [form, setForm] = useState<AdminAddMemberRequest>({ username: '', name: '', phone: '', email: '', password: '', role: 'MANAGER' });
  const [usernameValid, setUsernameValid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof AdminAddMemberRequest>(k: K, v: AdminAddMemberRequest[K]) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (!usernameValid) {
      setError('Please choose a valid available username before submitting.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.adminGarageAddMember(garageId, {
        ...form,
        username: form.username.trim().toLowerCase(),
      }, token);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <form className="admin-modal" onSubmit={onSubmit}>
        <div className="admin-modal-header"><h3>Add Member</h3></div>
        <div className="admin-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <label>
            Username
            <input
              className="admin-input"
              required
              value={form.username}
              onChange={(e) => set('username', e.target.value)}
              dir="ltr"
              spellCheck={false}
              autoComplete="off"
              placeholder="e.g. mechanic-bob"
            />
            <UsernameAvailability value={form.username} onValidityChange={setUsernameValid} />
          </label>
          <label>Name<input className="admin-input" required value={form.name} onChange={(e) => set('name', e.target.value)} /></label>
          <label>Phone<input className="admin-input" required value={form.phone} onChange={(e) => set('phone', e.target.value)} /></label>
          <label>Email<input className="admin-input" type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} /></label>
          <label>Initial password<input className="admin-input" type="password" required minLength={6} value={form.password} onChange={(e) => set('password', e.target.value)} /></label>
          <label>Role
            <select className="admin-input" value={form.role} onChange={(e) => set('role', e.target.value as GarageMemberRole)}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          {error && <div className="admin-error">{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={saving || !usernameValid}>{saving ? 'Adding…' : 'Add member'}</button>
        </div>
      </form>
    </div>
  );
}
